/**
 * Opens an HTTP long-poll to the agent-bot orchestrator's `/events`
 * endpoint, reads its NDJSON body, and dispatches each line to a
 * handler. Reconnects with capped exponential backoff after a
 * disconnect; stops cleanly when the caller aborts.
 *
 * Wire-protocol contract: each server emit is a JSON object on its own
 * line (`\n`-terminated). Empty lines are skipped. Partial lines
 * across chunk boundaries are buffered and parsed once complete.
 */

export interface NDJSONStreamOptions {
  /** Stream URL. */
  url: string;
  /** Fires once per parsed event in arrival order. */
  onEvent: (event: unknown) => void;
  /**
   * Fires on a network error, an HTTP non-2xx response, a missing
   * response body, or a malformed NDJSON line. Surfacing is
   * best-effort — the client keeps running (reconnects or skips the
   * bad line) so a single failure doesn't tear down the stream.
   * Callers that want to stop after N consecutive errors should
   * count them and abort the signal themselves.
   */
  onError?: (error: Error) => void;
  /**
   * Required. Aborting this signal stops the loop and any pending
   * reconnect.
   */
  signal: AbortSignal;
  /**
   * Resolved before every connect attempt so a fresh auth token can
   * be attached per reconnect. A rejection is swallowed — the attempt
   * proceeds without headers and the server decides.
   */
  getHeaders?: () => Promise<Record<string, string>>;
  /**
   * Fires when a connect attempt is rejected with HTTP 401, before
   * the error is surfaced via `onError` — lets the caller drop a
   * cached token so the next attempt re-authenticates.
   */
  onUnauthorized?: () => void;
  /** First reconnect delay. Doubles per attempt up to `maxBackoffMs`. */
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  /**
   * Abort the current attempt and reconnect if no bytes arrive for this
   * long. Guards against a silently black-holed connection (no RST, so
   * `reader.read()` would otherwise hang forever with no error). The
   * server heartbeats every 30s, so any healthy connection produces a
   * frame well inside the default window; a fired watchdog is surfaced
   * via `onError` and then follows the normal reconnect path.
   */
  idleTimeoutMs?: number;
  /** Test injection point. Defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
}

const DEFAULT_INITIAL_BACKOFF_MS = 1000;
const DEFAULT_MAX_BACKOFF_MS = 30000;
// 2 missed 30s server heartbeats + grace before declaring the
// connection dead.
const DEFAULT_IDLE_TIMEOUT_MS = 75000;

function tryDispatch(
  line: string,
  onEvent: (event: unknown) => void,
  onError: ((error: Error) => void) | undefined,
) {
  // Only the parse step is guarded — `onEvent` errors propagate so a
  // bug in the consumer isn't mis-reported as a malformed-line parse
  // failure (and doesn't silently inflate the success counter).
  let event: unknown;

  try {
    event = JSON.parse(line);
  } catch (parseErr) {
    onError?.(
      parseErr instanceof Error ? parseErr : new Error(String(parseErr)),
    );

    return;
  }

  onEvent(event);
}

/**
 * Parse every complete line in `buffer`, dispatching each event.
 * Returns the unparsed tail (anything after the last newline). Honors
 * an abort signal between dispatches so a consumer that aborts inside
 * `onEvent` doesn't continue receiving events from the same chunk.
 */
function drainLines(
  buffer: string,
  onEvent: (event: unknown) => void,
  onError: ((error: Error) => void) | undefined,
  signal: AbortSignal,
): string {
  const lines = buffer.split('\n');
  const tail = lines.pop() ?? '';

  for (const line of lines) {
    if (signal.aborted) break;
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    tryDispatch(trimmed, onEvent, onError);
  }

  return tail;
}

/**
 * Resolve after `ms` milliseconds, or immediately if the abort signal
 * fires during the wait.
 */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();

  return new Promise((resolve) => {
    const onAbort = () => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve();
    };

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Start consuming the NDJSON event stream. The returned promise
 * resolves once the signal aborts. Callers typically don't await it
 * — they fire-and-forget and rely on the abort signal for teardown.
 */
export async function startNDJSONStream({
  url,
  onEvent,
  onError,
  signal,
  getHeaders,
  onUnauthorized,
  initialBackoffMs = DEFAULT_INITIAL_BACKOFF_MS,
  maxBackoffMs = DEFAULT_MAX_BACKOFF_MS,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  fetchImpl = fetch,
}: NDJSONStreamOptions): Promise<void> {
  let attempt = 0;

  while (!signal.aborted) {
    let consumedEvents = 0;
    let idleTimedOut = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    let currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    // Per-attempt controller: the watchdog aborts just this attempt,
    // leaving the caller's signal (and thus the reconnect loop) alive.
    const attemptController = new AbortController();
    const onCallerAbort = () => attemptController.abort();

    signal.addEventListener('abort', onCallerAbort, { once: true });

    const onIdleTimeout = () => {
      idleTimedOut = true;
      // Abort covers the pre-response phase (fetch in flight); the
      // reader cancel covers a hung `read()`, whose promise a plain
      // fetch-abort doesn't settle in every environment.
      attemptController.abort();
      currentReader?.cancel().catch(() => {});
    };

    const armIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(onIdleTimeout, idleTimeoutMs);
    };

    const wrappedOnEvent = (event: unknown) => {
      // Count only successful dispatches. If `onEvent` throws, the
      // outer catch reports it via `onError` and the backoff continues
      // to escalate (a consumer that always throws shouldn't keep
      // hammering the server at the initial delay).
      onEvent(event);
      consumedEvents += 1;
    };

    try {
      let headers: Record<string, string> | undefined;

      if (getHeaders) {
        try {
          headers = await getHeaders();
        } catch {
          headers = undefined;
        }
      }

      armIdleTimer();

      const response = await fetchImpl(url, {
        signal: attemptController.signal,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          onUnauthorized?.();
        }

        throw new Error(
          `NDJSON stream HTTP ${response.status} ${response.statusText}`,
        );
      }

      const { body } = response;

      if (!body) {
        throw new Error('NDJSON stream response had no body');
      }

      const reader = body.getReader();
      currentReader = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (!signal.aborted) {
          armIdleTimer();

          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          buffer = drainLines(buffer, wrappedOnEvent, onError, signal);
        }

        // Flush any partial multi-byte UTF-8 sequence the decoder is
        // still holding (rare but valid for NDJSON since the protocol
        // allows arbitrary UTF-8 inside JSON strings).
        buffer += decoder.decode();

        // Flush a trailing line without a final newline (EOF mid-line
        // is rare but possible if the server closes without flushing).
        // Skip if the caller aborted — we don't want to dispatch
        // events after teardown. Skip after an idle timeout too: a
        // partial line on a dead connection is truncated, not final.
        if (!signal.aborted && !idleTimedOut) {
          const tail = buffer.trim();

          if (tail.length > 0) {
            tryDispatch(tail, wrappedOnEvent, onError);
          }
        }
      } finally {
        try {
          reader.releaseLock();
        } catch {
          // Reader was already released or the body had errored.
        }
      }
    } catch (err) {
      if (signal.aborted) return;

      // A watchdog-aborted attempt surfaces as an AbortError (or a
      // cancelled read) — report the idle timeout below instead of the
      // incidental abort exception.
      if (!idleTimedOut) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      clearTimeout(idleTimer);
      signal.removeEventListener('abort', onCallerAbort);
    }

    if (signal.aborted) return;

    if (idleTimedOut) {
      onError?.(
        new Error(
          `NDJSON stream idle timeout: no data received for ${idleTimeoutMs}ms`,
        ),
      );
    }

    // Reset backoff only if this attempt produced at least one event.
    // A fast-close loop with no events should keep escalating so we
    // don't hammer a misbehaving server.
    if (consumedEvents > 0) {
      attempt = 0;
    }

    const backoffMs = Math.min(initialBackoffMs * 2 ** attempt, maxBackoffMs);

    attempt += 1;
    await wait(backoffMs, signal);
  }
}
